import { ReportsNdviIcon } from '@assets/icons/minor/ReportsNdviIcon';
import { cn } from '@components/shadcn/utils';
import { ImageryButton } from '@components/shared/ImageryButton/ImageryButton';
import { useNotificationsCenter } from '@contexts/NotificationsCenterContext';
import { useSystemLanguage } from '@contexts/SystemLanguageContext';
import { SupportedEntitlements } from '@core/models/entitlements';
import { useUserNotifications } from '@hooks/queries/notifications/useUserNotifications';
import { useEntitlements } from '@hooks/useEntitlements';
import { useImageryFlags } from '@hooks/useImageryFlags';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Amplitude } from 'src/amplitude';
import { NotificationCenter } from '../../NotificationCenter.models';
import { buildReportUrl } from '../../reportUrl.utils';
import { BaseNotification } from '../BaseNotification';

function OrganizationWeeklyFieldInsightsAction({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const { t } = useTranslation();
  const entitlements = useEntitlements();
  const { data } = useUserNotifications();
  const { FlagReportAiSummary } = useImageryFlags();
  const [appLanguage] = useSystemLanguage();
  const { markAsRead } = useNotificationsCenter();
  const details = data?.[notification.id];
  const hasAccessToAiSummary =
    FlagReportAiSummary || entitlements.has(SupportedEntitlements.WEEKLY_FIELD_INSIGHTS_REPORT);

  const onClick = useCallback(() => {
    const reportUrl =
      buildReportUrl({
        path: 'report',
        reportKey: details?.user_notification.content.report_key,
        appLanguage,
      }) + (hasAccessToAiSummary ? '&include=insights' : '');

    Amplitude.trackNotificationsWeeklyInsightsOpenReport(hasAccessToAiSummary);
    window.open(reportUrl, '_blank');
    markAsRead(notification);
  }, [details, appLanguage, hasAccessToAiSummary, notification, markAsRead]);

  if (details) {
    return (
      <React.Fragment>
        <p className="text-sm text-gray-700 whitespace-pre-line mt-1">
          {t(`notifications.${notification.service}.${notification.trigger}`, {
            organization_name: details.user_notification.content.organization_name,
          })}
        </p>
        <div className="flex gap-3 mt-2">
          <ImageryButton type="softPrimary" onClick={onClick}>
            {t('notifications.actions.show_me')}
          </ImageryButton>
        </div>
      </React.Fragment>
    );
  }

  return null;
}

const ActionComponents: {
  [key: string]: React.FC<{ readonly notification: NotificationCenter.Notification }>;
} = {
  single_org: OrganizationWeeklyFieldInsightsAction,
} as const;

export function WeeklyFieldsInsightsNotification({
  notification,
}: {
  readonly notification: NotificationCenter.Notification;
}) {
  const ActionComponent = ActionComponents[notification.trigger];

  const icon = (
    <div
      className={cn('p-1 bg-sy-blue-10 rounded-lg', {
        ['bg-white']: notification.status === 'UNREAD',
      })}
    >
      <ReportsNdviIcon width={24} height={24} fill="#217C74" />
    </div>
  );
  return (
    <BaseNotification notification={notification} icon={icon} ActionComponent={ActionComponent} />
  );
}
